// src/keycloak.ts
import Keycloak from 'keycloak-js';
import type { KeycloakConfig, KeycloakInstance } from 'keycloak-js'; // Import types

// Singleton cache to prevent "Keycloak instance can only be initialized once" error
let cachedKeycloakInstance: KeycloakInstance | null = null;
let cachedRealm: string | null = null;
let isInitialized = false;
let initPromise: Promise<boolean> | null = null;

// This function creates and returns a Keycloak instance.
// It caches the instance to prevent re-initialization errors on React re-renders.
export const createKeycloakInstance = (realm: string): KeycloakInstance => {
  // Return cached instance if realm matches
  if (cachedKeycloakInstance && cachedRealm === realm) {
    console.log('[KEYCLOAK] Returning cached Keycloak instance for realm:', realm);
    return cachedKeycloakInstance;
  }

  // If realm changed, clear the cache (user switched firms)
  if (cachedRealm && cachedRealm !== realm) {
    console.log('[KEYCLOAK] Realm changed from', cachedRealm, 'to', realm, '- clearing cache');
    cachedKeycloakInstance = null;
    cachedRealm = null;
    isInitialized = false;
    initPromise = null;
  }

  if (!realm || realm.trim() === '') {
    // This case should ideally be prevented by the UI that calls this function.
    // However, as a fallback, we'll log an error and return a non-functional
    // or minimally configured instance to prevent a hard crash.
    // In a real app, you might throw an error or handle this more gracefully.
    console.error(
      'CRITICAL: Realm name is empty or invalid. Keycloak cannot be initialized properly.'
    );
    // Return a dummy object that matches KeycloakInstance type to avoid TS errors
    // This instance will likely fail if any Keycloak methods are called on it.
    return {
      init: () => Promise.reject(new Error('Realm not provided')),
      login: () => console.error('Cannot login: Keycloak not properly initialized.'),
      logout: () => console.error('Cannot logout: Keycloak not properly initialized.'),
      // Add other essential methods as dummy functions if needed by your types
      // This is a very basic mock to satisfy the type.
    } as unknown as KeycloakInstance; // Type assertion as a last resort for dummy
  }

  const keycloakConfig: KeycloakConfig = {
    url: 'https://keycloak.edgelex.com/auth', // Your Keycloak server URL
    realm: realm,                             // Realm comes from user input/storage
    clientId: 'edgelex-webapp',               // Your frontend's Client ID in Keycloak
  };

  console.log('[KEYCLOAK] Creating new Keycloak instance for realm:', realm);
  const keycloakInstance = new Keycloak(keycloakConfig);
  
  // Wrap the init method to make it idempotent (prevent "can only be initialized once" error)
  const originalInit = keycloakInstance.init.bind(keycloakInstance);
  keycloakInstance.init = (initOptions) => {
    // If already initialized, trigger callbacks manually and return resolved promise
    if (isInitialized) {
      console.log('[KEYCLOAK] Already initialized, triggering callbacks manually. authenticated:', keycloakInstance.authenticated);
      
      // Use setTimeout to ensure callbacks fire after the provider has set them up
      setTimeout(() => {
        if (keycloakInstance.authenticated) {
          console.log('[KEYCLOAK] Triggering onAuthSuccess callback');
          keycloakInstance.onAuthSuccess?.();
        }
        console.log('[KEYCLOAK] Triggering onReady callback');
        keycloakInstance.onReady?.(keycloakInstance.authenticated);
      }, 0);
      
      return Promise.resolve(keycloakInstance.authenticated ?? false);
    }
    console.log('[KEYCLOAK] Initializing Keycloak...');
    isInitialized = true;
    initPromise = originalInit(initOptions);
    return initPromise;
  };
  
  cachedKeycloakInstance = keycloakInstance;
  cachedRealm = realm;
  
  return cachedKeycloakInstance;
};

// Helper to clear the cached instance (useful for logout/firm switch)
export const clearKeycloakInstance = (): void => {
  console.log('[KEYCLOAK] Clearing cached instance');
  cachedKeycloakInstance = null;
  cachedRealm = null;
  isInitialized = false;
  initPromise = null;
};